import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accountQuery,
  getAccount,
  getUserSettings,
  hostBaseUrl,
  useAuthSession,
  useConfirmLostBonus,
  useLostBonuses,
} from '@/api';
import { cmsPageQuery } from '@/api/queries';
import {
  isLostBonusEnabled,
  type LostBonusOffer,
} from '@/domain/lost-bonuses';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { ClaimBonusPanel, ClaimBonusTermsModal } from '@/ui/profile';

export const Route = createFileRoute('/profile/claimbonus')({
  beforeLoad: () => {
    const account = getAccount();
    const user = getUserSettings();
    if (!isLostBonusEnabled(account, user)) {
      throw redirect({ to: '/profile' });
    }
  },
  component: ProfileClaimBonusPage,
});

export function ProfileClaimBonusPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const moduleEnabled = isLostBonusEnabled(account, user);
  const lostBonuses = useLostBonuses(moduleEnabled);
  const claim = useConfirmLostBonus();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [claimErrorKey, setClaimErrorKey] = useState<string | null>(null);
  const [termsSlug, setTermsSlug] = useState<string | null>(null);

  const accountGuid = account?.Guid?.trim() ?? '';
  const usersBaseUrl = hostBaseUrl('users');
  const termsQuery = useQuery({
    ...cmsPageQuery(accountGuid, termsSlug ?? '', selectedLanguage),
    enabled: Boolean(accountGuid && termsSlug && selectedLanguage),
  });

  const onSelect = useCallback((offer: LostBonusOffer) => {
    setSelectedId(offer.Id);
    setClaimErrorKey(null);
    const pageUrl = offer.PageUrl?.trim();
    if (pageUrl) {
      setTermsSlug(pageUrl);
    }
  }, []);

  const onConfirm = useCallback(async () => {
    if (!selectedId) {
      setClaimErrorKey('PleaseSelectBonus');
      return;
    }
    setClaimErrorKey(null);
    try {
      const response = await claim.confirm(selectedId);
      if (response?.Success) {
        setClaimed(true);
        return;
      }
      setClaimErrorKey(response?.Error?.trim() || 'SomethingWentWrong');
    } catch (err) {
      setClaimErrorKey(
        err instanceof Error && err.message
          ? err.message
          : 'SomethingWentWrong',
      );
    }
  }, [claim, selectedId]);

  if (!moduleEnabled) {
    return (
      <div className="flex flex-col gap-4 lg:gap-6">
        <header className="flex flex-col gap-1 lg:gap-2">
          <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
            {label('ClaimBonus', 'Claim bonus')}
          </h1>
          <p className="text-[13px] text-text-secondary">
            {label(
              'This offer is not available for your account.',
              'This offer is not available for your account.',
            )}
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <header className="flex flex-col gap-1 lg:gap-2">
        <p className="text-[13px] text-text-secondary lg:hidden">
          <Link to="/profile" className="hover:text-text-primary">
            ← {label('Account', 'Account')}
          </Link>
        </p>
        <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
          {label('ClaimBonus', 'Claim bonus')}
        </h1>
        <p className="text-[13px] text-text-secondary">
          {label(
            'Choose an eligible lost-bonus offer and confirm',
            'Choose an eligible lost-bonus offer and confirm',
          )}
        </p>
      </header>

      <ClaimBonusPanel
        offers={lostBonuses.offers}
        selectedId={selectedId}
        onSelect={onSelect}
        onConfirm={() => {
          void onConfirm();
        }}
        isLoading={lostBonuses.isLoading}
        isConfirming={claim.isConfirming}
        claimed={claimed}
        listError={lostBonuses.listError}
        claimErrorKey={claimErrorKey}
        onDismissClaimError={() => setClaimErrorKey(null)}
        label={label}
      />

      <ClaimBonusTermsModal
        open={Boolean(termsSlug)}
        onClose={() => setTermsSlug(null)}
        page={termsQuery.data ?? null}
        isLoading={termsQuery.isLoading}
        usersBaseUrl={usersBaseUrl}
        accountGuid={accountGuid}
        label={label}
      />
    </div>
  );
}
